import React from 'react'
import { ButtonModel, IconRegularClock, IconRegularClose } from '@notino/react-styleguide'
import * as Sentry from '@sentry/react'
import { useIntl } from 'react-intl'
import * as SC from './SalonServiceDetailStyles'
import RenderDuration from '../RenderDuration/RenderDuration'
import useMessages from '../../hooks/useMessages'
import { ServiceCardCallbackData, ServiceDetailData } from '../../types/types'
import { SERVICE_TYPE } from '../../utils/enums'
import UsersIcon from '../../assets/icons/UsersIcon'
import { renderOccupancyCount } from '../../utils/helper'
import Modal, { ModalModel } from '../../atoms/Modal/Modal'

type Props = {
	onClose: () => void
	phoneNumber: string
	serviceData: ServiceDetailData
	isReservationBookingModal?: boolean
	onServiceCardButtonClick: (data: ServiceCardCallbackData) => void
}

const SalonServiceDetail = (props: Props) => {
	const { messages } = useMessages()
	const { formatMessage } = useIntl()
	const { onClose, serviceData, phoneNumber, onServiceCardButtonClick, isReservationBookingModal = false } = props

	const { durationFrom, durationTo, priceFrom, priceTo, priceFromSecondary, priceToSecondary } = serviceData.service.rangePriceAndDurationData
	const { availableForReservation, id: serviceID, serviceCategoryParameter } = serviceData.service
	const description = serviceData.service.description || serviceData.category.description
	const isFullyBookedGroupReservation = serviceData.service.type === SERVICE_TYPE.CUSTOM_GROUP && serviceData.service.isFullyBooked
	const serviceName = serviceData.category.name
	const bookServiceAriaLabel = serviceName
		? formatMessage({ id: 'Book service {serviceName}', defaultMessage: 'Book service {serviceName}' }, { serviceName })
		: undefined
	const callServiceAriaLabel = serviceName
		? formatMessage({ id: 'Call about service {serviceName}', defaultMessage: 'Call about service {serviceName}' }, { serviceName })
		: undefined

	const renderBookingButton = () => {
		if (isFullyBookedGroupReservation) {
			return (
				<SC.BookOnlineButton buttonStyle={ButtonModel.Styles.secondary} fullWidth={true} disabled={true}>
					{messages['Sold out']}
				</SC.BookOnlineButton>
			)
		}
		if (availableForReservation) {
			return (
				<SC.BookOnlineButton
					buttonStyle={ButtonModel.Styles.primary}
					fullWidth={true}
					aria-label={bookServiceAriaLabel}
					onClick={(e) => {
						e.stopPropagation()
						onServiceCardButtonClick({
							industryID: serviceData.industryID,
							serviceID,
							serviceCategoryParameterID: serviceCategoryParameter?.id ?? null,
							gaSpecificData: { secondLevelCategoryID: serviceData.secondLevelCategoryID, serviceCategoryID: serviceData.category.id }
						})
						onClose()
					}}
				>
					{messages['Book online']}
				</SC.BookOnlineButton>
			)
		}
		return (
			<SC.BookOnlineButton
				buttonStyle={ButtonModel.Styles.primary}
				fullWidth={true}
				aria-label={callServiceAriaLabel}
				onClick={(e) => {
					e.stopPropagation()
					// Create a URL for the phone call
					const phoneUrl = `tel:${phoneNumber}`
					// Attempt to make the call
					try {
						window.location.href = phoneUrl
					} catch (error) {
						// TODO: dorobit modal s telefonnym cislom
						Sentry.captureException(error, { extra: { identifier: 'Phone number button click' } })
					}
				}}
			>
				{messages.Call}
			</SC.BookOnlineButton>
		)
	}

	const content = (
		<SC.ScrollContainer $isReservationBookingModal={isReservationBookingModal}>
			<SC.ContentWrapper>
				<SC.CloseButton type={'button'} onClick={onClose} aria-label={messages['Close modal']}>
					<IconRegularClose color={'icon.primary'} width={'1.5rem'} height={'1.5rem'} aria-hidden />
				</SC.CloseButton>

				<SC.ServiceCategoryTitle>{serviceData.category.name}</SC.ServiceCategoryTitle>

				<SC.PriceAndDurationContainer>
					{serviceData.service.type === SERVICE_TYPE.CUSTOM_GROUP && (
						<>
							<SC.OccupancyContainer>
								<UsersIcon />
								<SC.OccupancyLabel>
									{renderOccupancyCount(
										serviceData.service.maxReservationsCountRange?.min,
										serviceData.service.maxReservationsCountRange?.max,
										serviceID
									)}
								</SC.OccupancyLabel>
							</SC.OccupancyContainer>
							<SC.DotDivider />
						</>
					)}
					{durationFrom !== undefined && (
						<>
							<SC.ServiceDuration>
								<SC.DurationIcon>
									<IconRegularClock color={'icon.primary'} />
								</SC.DurationIcon>
								<SC.DurationText>
									<RenderDuration from={durationFrom} to={durationTo} />
								</SC.DurationText>
							</SC.ServiceDuration>
							<SC.DotDivider />
						</>
					)}
					<SC.ServicePrice from={priceFrom} to={priceTo} fromSecondary={priceFromSecondary} toSecondary={priceToSecondary} />
				</SC.PriceAndDurationContainer>

				{description && <SC.ServiceDescription>{description}</SC.ServiceDescription>}

				<SC.ButtonsWrapper>{renderBookingButton()}</SC.ButtonsWrapper>
			</SC.ContentWrapper>
		</SC.ScrollContainer>
	)

	if (isReservationBookingModal) {
		return (
			<SC.ReservationModalScreenWrapper>
				<SC.ReservationModalScreen>{content}</SC.ReservationModalScreen>
			</SC.ReservationModalScreenWrapper>
		)
	}

	return (
		<Modal
			layout={'fullScreenOnMobileCustom'}
			hideDefaultCloseButton
			desktopMaxWidth={500}
			breakpoint={ModalModel.ModalBreakpoints.md}
			show
			onClose={onClose}
			noBorders
			ariaLabel={serviceData.category.name}
		>
			{content}
		</Modal>
	)
}

export default SalonServiceDetail
